import { useState } from 'react';

import { Button, Stack, Typography } from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { useMutation } from '@redwoodjs/web';

import { Card, Spacer } from 'src/components';
import { FileUploadSelect } from 'src/components/FormikComponents';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { ALLOCATE_PAYMENTS } from 'src/graphql/mutations/allocatePayments.mutation';
import { PageContent } from 'src/layouts/MainLayout/PageContent';

const PaymentsFileAllocationPage = () => {
    const [fileUrl, setFileUrl] = useState('');
    const [originalFilename, setOriginalFilename] = useState('');
    const [resetKey, setResetKey] = useState(0);

    const [allocatePayments] = useMutation(ALLOCATE_PAYMENTS, {
        onError(error) {
            showErrorToast(error.message ?? 'An error occurred allocating payments');
        },
        onCompleted: ({ allocatePayments }) => {
            showSuccessToast(allocatePayments?.message ?? 'Payment allocations is complete');
            clearUploadedFile();
        },
    });

    const processPaymentFile = async (
        _values: unknown,
        { resetForm }: FormikHelpers<{ paymentAllocationFile: string }>
    ) => {
        await allocatePayments({ variables: { input: { fileUrl, originalFilename } } });
        resetForm();
    };

    const fileUploaded = (_signedUrl: string, urlOnly: string, filename: string) => {
        setFileUrl(urlOnly);
        setOriginalFilename(filename);
    };

    const clearUploadedFile = () => {
        setFileUrl('');
        setOriginalFilename('');
        setResetKey((prev) => prev + 1);
    };

    return (
        <PageContent heading="Allocate Payments">
            <Stack width="100%">
                <Formik
                    initialValues={{
                        paymentAllocationFile: '',
                    }}
                    onSubmit={processPaymentFile}
                    validationSchema={Yup.object().shape({
                        paymentAllocationFile: Yup.string().required(
                            'Payment allocation file is required'
                        ),
                    })}
                >
                    {({ isSubmitting }) => {
                        return (
                            <Form data-testid="payments-file-allocation-form">
                                <Card>
                                    <Stack
                                        width="100%"
                                        spacing={4}
                                        data-testid="payments-file-allocation-wrapper"
                                    >
                                        <Stack width="100%" spacing={0.5}>
                                            <Typography fontSize={15} fontWeight={600}>
                                                Payment Allocation File
                                            </Typography>

                                            <Typography fontSize={12} fontWeight={600}>
                                                Upload the latest payment allocation file for
                                                processing
                                            </Typography>
                                        </Stack>

                                        <Stack
                                            width="50%"
                                            data-testid="payments-file-allocation-form-file-upload-wrapper"
                                        >
                                            <FileUploadSelect
                                                key={resetKey}
                                                name="paymentAllocationFile"
                                                label="Upload Payment File"
                                                acceptedFileTypes={{
                                                    'application/vnd.oasis.opendocument.spreadsheet':
                                                        [
                                                            '.ods',
                                                            '.ODS',
                                                            '.oDs',
                                                            '.odS',
                                                            '.oDS',
                                                            '.OdS',
                                                        ],
                                                    'application/vnd.ms-excel': ['.xls', '.XLS'],
                                                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                                                        [
                                                            '.xlsx',
                                                            '.XLSX',
                                                            '.xSlx',
                                                            '.xsLx',
                                                            '.xSlX',
                                                            '.Xslx',
                                                        ],
                                                    'text/csv': [
                                                        '.csv',
                                                        '.Csv',
                                                        '.cSv',
                                                        '.csV',
                                                        '.CSV',
                                                    ],
                                                }}
                                                callback={fileUploaded}
                                            />
                                        </Stack>
                                    </Stack>
                                </Card>
                                <Spacer padding={2} />
                                <Stack
                                    data-testid="payments-file-allocation-form-button-wrapper"
                                    width="100%"
                                    justifyContent="flex-end"
                                    direction="row"
                                >
                                    <Button
                                        className="btn-dark btn-medium"
                                        loadingPosition="start"
                                        type="submit"
                                        variant="contained"
                                        loading={isSubmitting}
                                        disableElevation
                                    >
                                        {isSubmitting
                                            ? 'Processing Payment File'
                                            : 'Process Payment File'}
                                    </Button>
                                </Stack>
                            </Form>
                        );
                    }}
                </Formik>
            </Stack>
        </PageContent>
    );
};
export default PaymentsFileAllocationPage;
